// 計測に送る URL から個人情報を落とす。通すクエリは許可リストの6つだけ。
// name、email、fbclid、トークンなどは全部落とす。
// apps/women-lp/src/client/analytics-url.ts に URL 整形の写しがある (PostHog 用の部分は写していない)。women-astro は @/lib から直接読む。直すときは両方そろえる。
export const ANALYTICS_URL_ALLOWED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "from",
] as const;

const ALLOWED_VALUE = /^[A-Za-z0-9_.-]{1,64}$/;

// from や utm の値を計測の項目へ取り出すときの検査。合わない値は undefined にする。
export function safeParamValue(value: string | null | undefined): string | undefined {
  return value && ALLOWED_VALUE.test(value) ? value : undefined;
}

export function sanitizeAnalyticsUrl(raw: string): string {
  try {
    const url = new URL(raw);
    const kept = new URLSearchParams();
    for (const key of ANALYTICS_URL_ALLOWED_PARAMS) {
      for (const value of url.searchParams.getAll(key)) {
        // メールや名前が紛れ込まない形の値だけ残す。合わない値はキーごと落とす
        if (ALLOWED_VALUE.test(value)) kept.append(key, value);
      }
    }
    const query = kept.toString();
    return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
  } catch {
    // 解釈できない文字列はクエリとハッシュを切り落とす
    return raw.split(/[?#]/)[0] ?? "";
  }
}

// referrer 用。空なら undefined を返す。
export function sanitizeReferrer(raw: string | null | undefined): string | undefined {
  return raw ? sanitizeAnalyticsUrl(raw) : undefined;
}

// PostHog のイベントで URL を持つ項目。properties、$set、$set_once のどれにも同じ一覧を当てる
export const POSTHOG_URL_KEYS = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_referrer",
] as const;

type PosthogEvent = {
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
} | null;

function sanitizeUrlBag(bag: Record<string, unknown> | undefined): void {
  if (!bag) return;
  for (const key of POSTHOG_URL_KEYS) {
    const value = bag[key];
    // $direct のような印は URL ではないので触らない
    if (typeof value === "string" && /^https?:\/\//.test(value)) {
      bag[key] = sanitizeAnalyticsUrl(value);
    }
  }
}

// posthog.init の before_send に渡す。受け取ったイベントを書き換えて返す
export function sanitizePosthogEvent<T extends PosthogEvent>(event: T): T {
  if (!event) return event;
  sanitizeUrlBag(event.properties);
  sanitizeUrlBag(event.$set);
  sanitizeUrlBag(event.$set_once);
  return event;
}
